import '@material/web/textfield/outlined-text-field.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';

import { api, type LedgerEntry, type Reward } from '../api.js';
import { ce, el, clear, fmtMinutes, type ValueEl } from '../dom.js';
import { getLocale, t } from '../i18n.js';

export function renderWallet(): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const balanceEl = el('div', { class: 'balance-big', id: 'balance' }, '…');
  const rewardsEl = el('div', {});
  const historyEl = el('div', {});

  function load(): void {
    api.get<{ balanceMinutes: number }>('/api/balance').then(({ balanceMinutes }) => {
      balanceEl.textContent = fmtMinutes(balanceMinutes);
    });
    api.get<{ rewards: Reward[] }>('/api/rewards').then(({ rewards }) => {
      clear(rewardsEl);
      const active = rewards.filter((r) => r.active);
      if (active.length === 0) {
        rewardsEl.append(el('p', { class: 'muted' }, t('walletNoRewards')));
      }
      for (const r of active) {
        rewardsEl.append(
          el(
            'div',
            { class: 'task' },
            el('div', {}, el('div', { class: 'title' }, r.title), el('div', { class: 'meta' }, fmtMinutes(r.costMinutes))),
            el(
              'button',
              {
                onclick: () => {
                  error.textContent = '';
                  api
                    .post<{ pending?: boolean }>('/api/spend', { rewardId: r.id })
                    .then((res) => {
                      if (res.pending) error.textContent = t('sentForApproval');
                      load();
                    })
                    .catch((err: Error) => {
                      error.textContent = err.message;
                    });
                },
              },
              t('walletBuy'),
            ),
          ),
        );
      }
    });
    api.get<{ entries: LedgerEntry[] }>('/api/ledger').then(({ entries }) => {
      clear(historyEl);
      const table = el('table', { class: 'ledger' });
      for (const e of [...entries].reverse().slice(0, 30)) {
        table.append(
          el(
            'tr',
            {},
            el('td', {}, new Date(e.createdAt).toLocaleDateString(getLocale())),
            el('td', {}, e.note || e.type),
            el(
              'td',
              { class: `num ${e.deltaMinutes >= 0 ? 'delta-pos' : 'delta-neg'}` },
              `${e.deltaMinutes > 0 ? '+' : ''}${fmtMinutes(e.deltaMinutes)}`,
            ),
          ),
        );
      }
      historyEl.append(table);
    });
  }

  const spendMinutes = ce('md-outlined-text-field', {
    id: 'freeform-minutes', label: t('walletSpendAmount'), type: 'number', min: '1', 'suffix-text': t('composeMinSuffix'),
  }) as ValueEl;
  const spendNote = ce('md-outlined-text-field', {
    id: 'freeform-note', label: t('walletSpendNote'), placeholder: t('walletFreeformPlaceholder'),
  }) as ValueEl;
  const freeform = el(
    'form',
    {
      onsubmit: (ev: Event) => {
        ev.preventDefault();
        error.textContent = '';
        api
          .post<{ pending?: boolean }>('/api/spend', {
            minutes: Number(spendMinutes.value),
            note: spendNote.value.trim(),
          })
          .then((res) => {
            spendMinutes.value = '';
            spendNote.value = '';
            if (res.pending) error.textContent = t('sentForApproval');
            load();
          })
          .catch((err: Error) => {
            error.textContent = err.message;
          });
      },
    },
    el('h2', {}, t('walletFreeformTitle')),
    el('div', { class: 'admin-form-fields' }, spendMinutes, spendNote),
    el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('walletSpend'))),
  );

  root.append(
    el('div', { class: 'card' }, el('h3', {}, t('walletBalance')), balanceEl),
    error,
    el('div', { class: 'card' }, el('h2', {}, t('walletRewards')), rewardsEl),
    el('div', { class: 'card' }, freeform),
    el('div', { class: 'card' }, el('h2', {}, t('walletHistory')), historyEl),
  );
  load();
  return root;
}
